import { Bike, ChefHat, Sparkles, Wifi, Plane } from "lucide-react";
import { fmtINR } from "@/lib/api";

const ICONS = { Bike, ChefHat, Sparkles, Wifi, Plane };

export default function AddonSelector({
  catalog,
  listingAddons,
  value,
  onChange,
}) {
  // value: { [key]: { billing_cycle, qty } }
  const listingMap = Object.fromEntries((listingAddons || []).map((a) => [a.key, a]));

  const toggle = (key, def) => {
    const next = { ...value };
    if (next[key]) {
      delete next[key];
    } else {
      const cycle = def.billing[0];
      next[key] = { billing_cycle: cycle, qty: 1 };
    }
    onChange(next);
  };

  const setCycle = (key, cycle) => {
    onChange({ ...value, [key]: { ...value[key], billing_cycle: cycle } });
  };

  const setQty = (key, qty) => {
    onChange({ ...value, [key]: { ...value[key], qty: Math.max(1, qty) } });
  };

  const priceFor = (def, cycle) => {
    const la = listingMap[def.key];
    if (la && la[cycle]) return la[cycle];
    return def.default?.[cycle] ?? 0;
  };

  const available = catalog.filter((c) => listingMap[c.key]?.enabled);

  if (available.length === 0) {
    return (
      <p className="text-sm" style={{ color: "var(--text-2)" }} data-testid="addons-empty">
        This host has not enabled any add-ons.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="addon-grid">
      {available.map((def) => {
        const Icon = ICONS[def.icon] || Sparkles;
        const selected = !!value[def.key];
        const cycle = value[def.key]?.billing_cycle || def.billing[0];
        const unit = priceFor(def, cycle);
        return (
          <div
            key={def.key}
            className="rounded-xl p-5 transition-all duration-300 cursor-pointer"
            style={{
              background: selected ? "rgba(34,60,51,0.05)" : "var(--bone)",
              border: selected ? "2px solid var(--forest)" : "1px solid var(--line)",
            }}
            onClick={() => toggle(def.key, def)}
            data-testid={`addon-card-${def.key}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="p-2.5 rounded-lg"
                style={{ background: selected ? "var(--forest)" : "var(--surface-alt)", color: selected ? "#fff" : "var(--forest)" }}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-[color:var(--text-1)]">{def.label}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>
                  {def.description}
                </div>
              </div>
            </div>

            {selected && (
              <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap gap-2">
                  {def.billing.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCycle(def.key, c)}
                      className="text-xs px-3 py-1.5 rounded-full transition-all"
                      style={{
                        background: cycle === c ? "var(--forest)" : "transparent",
                        color: cycle === c ? "#fff" : "var(--forest)",
                        border: "1px solid var(--forest)",
                      }}
                      data-testid={`addon-${def.key}-cycle-${c}`}
                    >
                      {c.replace("_", " ")}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: "var(--text-2)" }}>Qty</span>
                    <input
                      type="number" min={1}
                      value={value[def.key]?.qty || 1}
                      onChange={(e) => setQty(def.key, parseInt(e.target.value || "1"))}
                      className="w-16 input-earthy !py-1.5 text-center"
                      data-testid={`addon-${def.key}-qty`}
                    />
                  </div>
                  <div className="text-sm font-medium text-[color:var(--forest)]" data-testid={`addon-${def.key}-unit`}>
                    {fmtINR(unit)} <span className="text-[color:var(--text-3)] text-xs">/ {cycle.replace("_", " ")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
